import React, { useState, useRef, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  Avatar,
  Paper,
  useTheme,
  InputAdornment,
  Chip,
  Fade,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import "./ChatPanel.css";
import {
  Close as CloseIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const StyledDrawer = styled(Drawer)(({ theme, ismobile }) => ({
  "& .MuiDrawer-paper": {
    width: ismobile === "true" ? "100vw" : 400,
    background:
      theme.palette.mode === "dark"
        ? "linear-gradient(180deg, #222d3b 0%, #1a2332 100%)"
        : "linear-gradient(180deg, #ffffff 0%, #f6fafd 100%)",
    borderLeft: `1px solid ${
      theme.palette.mode === "dark" ? "#2c3a4e" : "#e3eaf3"
    }`,
    boxShadow:
      theme.palette.mode === "dark"
        ? "-4px 0 24px rgba(0, 0, 0, 0.3)"
        : "-4px 0 24px rgba(2, 89, 165, 0.1)",
  },
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(135deg, #2c3a4e 0%, #233044 100%)"
      : "linear-gradient(135deg, #0259a5ff 0%, #1769aa 100%)",
  borderBottom: `1px solid ${
    theme.palette.mode === "dark" ? "#2c3a4e" : "#e3eaf3"
  }`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const ChatMessage = styled(Paper)(({ theme, isOwn }) => ({
  padding: theme.spacing(1.5),
  margin: theme.spacing(0.5, isOwn ? 0 : 1, 0.5, isOwn ? 1 : 0),
  maxWidth: "80%",
  alignSelf: isOwn ? "flex-end" : "flex-start",
  background: isOwn
    ? theme.palette.mode === "dark"
      ? "linear-gradient(135deg, #64b5f6 0%, #2286c3 100%)"
      : "linear-gradient(135deg, #0259a5ff 0%, #1769aa 100%)"
    : theme.palette.mode === "dark"
    ? "#2c3a4e"
    : "#f8f9fa",
  color: isOwn ? "#ffffff" : theme.palette.text.primary,
  borderRadius: isOwn ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 2px 8px rgba(0, 0, 0, 0.2)"
      : "0 2px 8px rgba(2, 89, 165, 0.08)",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 4px 12px rgba(0, 0, 0, 0.3)"
        : "0 4px 12px rgba(2, 89, 165, 0.12)",
  },
}));

const ChatInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 24,
    background:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(10px)",
    border: `1px solid ${
      theme.palette.mode === "dark" ? "#2c3a4e" : "#e3eaf3"
    }`,
    "&:hover": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    "&.Mui-focused": {
      border: `2px solid ${theme.palette.primary.main}`,
    },
  },
}));

// Mock chat data
const initialMessages = [
  {
    id: 1,
    text: "Hello! I need some information regarding the topic discussed here.",
    sender: "Admin",
    isOwn: false,
    timestamp: new Date(Date.now() - 5 * 60000),
    avatar: "A",
    isAdmin: true,
  },
  {
    id: 2,
    text: "Sure! I'll get back to you shortly.",
    isOwn: true,
    timestamp: new Date(Date.now() - 3 * 60000),
    avatar: "Y",
    isAdmin: false,
  },
];

/**
 * Chat Panel component for requesting more information
 * @param {object} props
 * @param {boolean} props.open - Whether the chat panel is open
 * @param {function} props.onClose - Function to close the chat panel
 * @param {string} [props.title] - Chat panel title
 */
const ChatPanel = ({ open, onClose, title }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [messages, setMessages] = useState(initialMessages);
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Use translated title as default if not provided
  const chatTitle = title || t("chatPanel.requestInformation");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      const message = {
        id: messages.length + 1,
        text: newMessage.trim(),
        sender: "You",
        isOwn: true,
        timestamp: new Date(),
        avatar: "Y",
        isAdmin: false,
      };
      setMessages((prev) => [...prev, message]);
      setNewMessage("");

      // Simulate admin response after 2 seconds
      setTimeout(() => {
        const adminResponse = {
          id: messages.length + 2,
          text: "Thank you for your question! I'll get back to you with the requested information shortly.",
          sender: "Admin",
          isOwn: false,
          timestamp: new Date(),
          avatar: "A",
          isAdmin: true,
        };
        setMessages((prev) => [...prev, adminResponse]);
      }, 2000);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <StyledDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ismobile={isMobile.toString()}
      className="chat-panel-drawer"
    >
      <Fade in={open} timeout={300}>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Chat Header */}
          <ChatHeader>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AdminIcon sx={{ color: "#fff", mr: 1 }} />
              <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
                {chatTitle}
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </ChatHeader>

          {/* Online Status */}
          <Box
            sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <Chip
              size="small"
              label={t("chatPanel.adminOnline")}
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark" ? "#2e7d32" : "#4caf50",
                color: "#fff",
                fontSize: "0.75rem",
              }}
            />
          </Box>

          {/* Messages Container */}
          <Box
            className="chat-messages-container"
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <List sx={{ p: 0, flex: 1 }}>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    display: "flex",
                    flexDirection: message.isOwn ? "row-reverse" : "row",
                    alignItems: "flex-start",
                    p: 0.5,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: "0.875rem",
                      bgcolor: message.isAdmin
                        ? theme.palette.primary.main
                        : theme.palette.secondary.main,
                      mx: 1,
                    }}
                  >
                    {message.isAdmin ? (
                      <AdminIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <PersonIcon sx={{ fontSize: 18 }} />
                    )}
                  </Avatar>
                  <Box sx={{ maxWidth: "100%" }}>
                    <ChatMessage
                      isOwn={message.isOwn}
                      elevation={1}
                      className="chat-message"
                    >
                      <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                        {message.text}
                      </Typography>
                    </ChatMessage>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        display: "block",
                        textAlign: message.isOwn ? "right" : "left",
                        mt: 0.5,
                        mx: 1,
                      }}
                    >
                      {formatTime(message.timestamp)}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <ChatInput
              ref={inputRef}
              fullWidth
              multiline
              maxRows={3}
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      sx={{
                        color: theme.palette.primary.main,
                        "&:disabled": {
                          color: theme.palette.text.disabled,
                        },
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size="small"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <AttachFileIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <EmojiIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: "block",
                mt: 1,
                textAlign: "center",
              }}
            >
              Press Enter to send, Shift+Enter for new line
            </Typography>
          </Box>
        </Box>
      </Fade>
    </StyledDrawer>
  );
};

export default ChatPanel;
